import { useEffect, useState } from "react";
import { Clock, Swords, X } from "lucide-react";
import { friendsStore, useFriends, type MatchInvite } from "../store/friendsStore";
import { avatarUrl } from "../auth/api";
import { GAME_BY_KEY } from "../lib/games";
import GameIcon from "./GameIcon";
import Avatar from "./Avatar";

/** Mirrors the server's PENDING_TTL_MS — a pending challenge expires 10
 *  minutes after it was sent if the friend hasn't accepted. */
const INVITE_TTL_MS = 10 * 60_000;

function mmss(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}

/**
 * Floating card(s) the sender sees after challenging a friend: opponent, game,
 * stake + pot, and a live countdown to the 10-minute expiry, with a cancel
 * button. Auto-hides the moment the invite leaves "pending" — accepted (the
 * sender is routed into the match), declined, cancelled, or expired.
 */
export default function OutgoingChallengeCard() {
  const friends = useFriends();
  const [now, setNow] = useState(() => Date.now());

  const pending = friends.matchInvitesOut.filter(
    (i) => i.status === "pending" && i.ts + INVITE_TTL_MS > now,
  );

  // Tick once a second only while a challenge is outstanding (drives the
  // countdown + the local expiry hide).
  useEffect(() => {
    if (pending.length === 0) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [pending.length]);

  if (pending.length === 0) return null;

  return (
    <div className="fixed left-5 bottom-5 z-[90] flex flex-col gap-3">
      {pending.map((inv) => (
        <Card key={inv.id} inv={inv} now={now} />
      ))}
    </div>
  );
}

function Card({ inv, now }: { inv: MatchInvite; now: number }) {
  const meta = GAME_BY_KEY[inv.game];
  const accent = meta?.accent ?? "#FF6B00";
  const remaining = inv.ts + INVITE_TTL_MS - now;
  const frac = Math.max(0, Math.min(1, remaining / INVITE_TTL_MS));
  // Calm in the accent colour, then amber, then red as it runs out.
  const barColor = remaining <= 30_000 ? "#F87272" : remaining <= 120_000 ? "#FBBF24" : accent;
  const pot = (inv.stake * 2 * 0.9).toFixed(2);

  return (
    <div
      className="w-[320px] rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(160deg, rgba(26,26,26,0.97) 0%, rgba(15,15,15,0.97) 100%)",
        border: `1px solid ${accent}55`,
        boxShadow: `0 12px 40px ${accent}30, 0 0 0 1px ${accent}12`,
        backdropFilter: "blur(14px)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5 border-b border-white/5"
        style={{ background: `linear-gradient(90deg, ${accent}22 0%, transparent 70%)` }}
      >
        <div className="flex items-center gap-2">
          <span
            className="flex items-center justify-center w-7 h-7 rounded-lg"
            style={{ background: `${accent}25`, color: accent }}
          >
            <GameIcon game={meta?.icon ?? "fps"} className="w-4 h-4" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] font-bold tracking-wider text-white/95 uppercase">
              Challenge sent
            </span>
            <span className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-white/40">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Waiting for accept
            </span>
          </div>
        </div>
        <button
          onClick={() => friendsStore.cancelInvite(inv.id)}
          className="text-white/30 hover:text-white/80 transition p-1"
          aria-label="Cancel challenge"
          title="Cancel challenge"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          <Avatar
            address={inv.address}
            handle={inv.handle}
            src={avatarUrl(inv.address, inv.avatarVersion)}
            size={40}
            className="ring-1 ring-white/10 shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] text-white font-semibold truncate">{inv.handle}</div>
            <div className="text-[11px] text-white/45 truncate">
              {meta?.label ?? inv.game} · ${inv.stake} stake
            </div>
          </div>
          <div className="text-right shrink-0">
            <div
              className="font-mono text-[18px] font-bold tabular-nums leading-none"
              style={{ color: barColor }}
            >
              {mmss(remaining)}
            </div>
            <div className="flex items-center justify-end gap-1 text-[9px] uppercase tracking-widest text-white/35 mt-1">
              <Clock className="w-2.5 h-2.5" /> left
            </div>
          </div>
        </div>

        {/* Countdown bar */}
        <div className="mt-3 h-1 rounded-full bg-white/8 overflow-hidden">
          <div
            className="h-full rounded-full transition-[width] duration-1000 ease-linear"
            style={{ width: `${frac * 100}%`, background: barColor }}
          />
        </div>

        {/* Footer */}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-white/45">
            Winner takes <span className="text-emerald-400 font-semibold">${pot}</span>
          </span>
          <button
            onClick={() => friendsStore.cancelInvite(inv.id)}
            className="px-3 py-1.5 rounded-md border border-white/10 text-white/60 hover:text-white hover:border-white/30 font-mono text-[10px] tracking-[0.14em] uppercase inline-flex items-center gap-1.5 transition-colors"
          >
            <Swords className="w-3 h-3" /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
